/**
 * Task Templates Page (story 22770)
 *
 * List, filter, edit and activate/deactivate the global task templates via
 * /api/TaskTemplates. There is no create or delete: the seed owns the catalogue.
 * Each save snapshots the prior version server-side and bumps the Version shown here.
 */

import { useCallback, useEffect, useMemo, useRef, useState, startTransition, type FormEvent } from 'react';
import { RefreshCw, AlertCircle, ListChecks, Edit2, Power, PowerOff, X, CheckCircle } from 'lucide-react';
import {
  listTaskTemplates,
  updateTaskTemplate,
  TaskCategory,
  TASK_CATEGORY_LABELS,
  ENRICHMENT_POLICIES,
  type TaskTemplate,
  type TaskTemplateUpdate,
} from '../services/taskTemplatesService';

const CATEGORY_OPTIONS = Object.values(TaskCategory).filter(
  (v): v is TaskCategory => typeof v === 'number',
);

const inputClass =
  'w-full px-3 py-2 rounded-lg bg-zinc-900 border border-zinc-700 text-sm text-zinc-200 focus:outline-none focus:ring-2 focus:ring-violet-500/50 focus:border-violet-500';
const selectClass =
  'px-3 py-2 rounded-lg bg-zinc-900 border border-zinc-700 text-sm text-zinc-200 focus:outline-none focus:ring-2 focus:ring-violet-500/50 focus:border-violet-500';

function categoryLabel(c: TaskCategory): string {
  return TASK_CATEGORY_LABELS[c] ?? `Category ${c}`;
}

/** Only the fields that differ from the loaded template; empty optional text clears to ''. */
function diffUpdate(original: TaskTemplate, next: Required<TaskTemplateUpdate>): TaskTemplateUpdate {
  const changes: TaskTemplateUpdate = {};
  if (next.title !== original.title) changes.title = next.title;
  if (next.description !== original.description) changes.description = next.description;
  if (next.estimatedHours !== original.estimatedHours) changes.estimatedHours = next.estimatedHours;
  if (next.priority !== original.priority) changes.priority = next.priority;
  if (next.isActive !== original.isActive) changes.isActive = next.isActive;
  if (next.outsourceEstimate !== (original.outsourceEstimate ?? '')) changes.outsourceEstimate = next.outsourceEstimate;
  if (next.domain !== (original.domain ?? '')) changes.domain = next.domain;
  if (next.preferredAgentId !== (original.preferredAgentId ?? '')) changes.preferredAgentId = next.preferredAgentId;
  if (next.enrichmentPolicy !== (original.enrichmentPolicy ?? '')) changes.enrichmentPolicy = next.enrichmentPolicy;
  return changes;
}

// ---------------------------------------------------------------------------
// Edit form
// ---------------------------------------------------------------------------

function TemplateEditForm({
  template,
  onSave,
  onCancel,
  saving,
}: {
  template: TaskTemplate;
  onSave: (changes: TaskTemplateUpdate) => Promise<void>;
  onCancel: () => void;
  saving: boolean;
}) {
  const [title, setTitle] = useState(template.title);
  const [description, setDescription] = useState(template.description);
  const [estimatedHours, setEstimatedHours] = useState(String(template.estimatedHours));
  const [priority, setPriority] = useState(String(template.priority));
  const [outsourceEstimate, setOutsourceEstimate] = useState(template.outsourceEstimate ?? '');
  const [domain, setDomain] = useState(template.domain ?? '');
  const [preferredAgentId, setPreferredAgentId] = useState(template.preferredAgentId ?? '');
  const [enrichmentPolicy, setEnrichmentPolicy] = useState(template.enrichmentPolicy ?? '');
  const [isActive, setIsActive] = useState(template.isActive);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    await onSave(
      diffUpdate(template, {
        title: title.trim(),
        description: description.trim(),
        estimatedHours: Number(estimatedHours),
        priority: Number(priority),
        isActive,
        outsourceEstimate: outsourceEstimate.trim(),
        domain: domain.trim(),
        preferredAgentId: preferredAgentId.trim(),
        enrichmentPolicy,
      }),
    );
  };

  const label = 'block text-xs font-medium text-zinc-400 mb-1';

  return (
    <form
      onSubmit={(e) => void handleSubmit(e)}
      aria-label={`Edit ${template.title}`}
      className="space-y-4 p-4 bg-zinc-800/50 rounded-lg border border-zinc-700"
    >
      <p className="text-xs text-zinc-500">
        {categoryLabel(template.category)} · Version {template.version}. Saving keeps the previous
        version in history.
      </p>

      <div>
        <label htmlFor="tt-title" className={label}>Title *</label>
        <input id="tt-title" required maxLength={100} value={title} onChange={(e) => setTitle(e.target.value)} className={inputClass} />
      </div>

      <div>
        <label htmlFor="tt-description" className={label}>Description *</label>
        <textarea id="tt-description" required maxLength={500} rows={3} value={description} onChange={(e) => setDescription(e.target.value)} className={`${inputClass} resize-y`} />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div>
          <label htmlFor="tt-hours" className={label}>Estimated hours</label>
          <input id="tt-hours" type="number" min={0} step="0.25" value={estimatedHours} onChange={(e) => setEstimatedHours(e.target.value)} className={inputClass} />
        </div>
        <div>
          <label htmlFor="tt-priority" className={label}>Priority (1-100, lower is higher)</label>
          <input id="tt-priority" type="number" min={1} max={100} value={priority} onChange={(e) => setPriority(e.target.value)} className={inputClass} />
        </div>
        <div>
          <label htmlFor="tt-outsource" className={label}>Outsource estimate</label>
          <input id="tt-outsource" value={outsourceEstimate} onChange={(e) => setOutsourceEstimate(e.target.value)} className={inputClass} />
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div>
          <label htmlFor="tt-domain" className={label}>Domain</label>
          <input id="tt-domain" value={domain} onChange={(e) => setDomain(e.target.value)} className={inputClass} />
        </div>
        <div>
          <label htmlFor="tt-agent" className={label}>Preferred agent id</label>
          <input id="tt-agent" value={preferredAgentId} onChange={(e) => setPreferredAgentId(e.target.value)} className={inputClass} />
        </div>
        <div>
          <label htmlFor="tt-enrichment" className={label}>Enrichment policy</label>
          <select id="tt-enrichment" value={enrichmentPolicy} onChange={(e) => setEnrichmentPolicy(e.target.value)} className={inputClass}>
            <option value="">Not set</option>
            {ENRICHMENT_POLICIES.map((p) => (
              <option key={p} value={p}>{p}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="flex items-center gap-2">
        <input id="tt-active" type="checkbox" checked={isActive} onChange={(e) => setIsActive(e.target.checked)} className="w-4 h-4 accent-violet-500" />
        <label htmlFor="tt-active" className="text-xs text-zinc-400">Active</label>
      </div>

      <div className="flex gap-2 pt-1">
        <button
          type="submit"
          disabled={saving || title.trim() === '' || description.trim() === ''}
          className="flex items-center gap-2 px-4 py-2 rounded-lg bg-violet-600 text-zinc-950 text-sm font-medium hover:bg-violet-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed min-h-[48px]"
        >
          {saving && <RefreshCw className="w-4 h-4 animate-spin" />}
          {saving ? 'Saving…' : 'Save Template'}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 rounded-lg bg-zinc-700 text-zinc-300 text-sm font-medium hover:bg-zinc-600 transition-colors min-h-[48px]"
        >
          Cancel
        </button>
      </div>
    </form>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export function TaskTemplatesPage() {
  const [templates, setTemplates] = useState<TaskTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editTarget, setEditTarget] = useState<TaskTemplate | null>(null);
  const [saving, setSaving] = useState(false);
  const [togglingId, setTogglingId] = useState<string | null>(null);
  const [savedMsg, setSavedMsg] = useState<string | null>(null);
  const savedMsgTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => () => {
    if (savedMsgTimerRef.current) clearTimeout(savedMsgTimerRef.current);
  }, []);

  const [filterCategory, setFilterCategory] = useState<TaskCategory | ''>('');
  const [filterActive, setFilterActive] = useState<'' | 'true'>('');
  const [search, setSearch] = useState('');

  const flashSaved = (msg: string) => {
    setSavedMsg(msg);
    if (savedMsgTimerRef.current) clearTimeout(savedMsgTimerRef.current);
    savedMsgTimerRef.current = setTimeout(() => setSavedMsg(null), 3000);
  };

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await listTaskTemplates({
        category: filterCategory === '' ? undefined : filterCategory,
        activeOnly: filterActive === 'true',
      });
      setTemplates(data.templates);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load task templates');
    } finally {
      setLoading(false);
    }
  }, [filterCategory, filterActive]);

  useEffect(() => {
    startTransition(() => {
      void load();
    });
  }, [load]);

  const applyUpdated = (updated: TaskTemplate) =>
    setTemplates((prev) => prev.map((t) => (t.id === updated.id ? updated : t)));

  const handleSave = async (changes: TaskTemplateUpdate) => {
    if (!editTarget) return;
    if (Object.keys(changes).length === 0) {
      setEditTarget(null);
      return;
    }
    setSaving(true);
    setError(null);
    try {
      applyUpdated(await updateTaskTemplate(editTarget.id, changes));
      setEditTarget(null);
      flashSaved('Template updated');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update template');
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = async (t: TaskTemplate) => {
    setTogglingId(t.id);
    setError(null);
    try {
      applyUpdated(await updateTaskTemplate(t.id, { isActive: !t.isActive }));
      flashSaved(t.isActive ? 'Template deactivated' : 'Template activated');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to change template state');
    } finally {
      setTogglingId(null);
    }
  };

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return q === ''
      ? templates
      : templates.filter((t) => `${t.title} ${t.description}`.toLowerCase().includes(q));
  }, [templates, search]);

  return (
    <main className="flex-1 p-6 overflow-y-auto">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <ListChecks className="w-5 h-5 text-violet-400" />
          <h1 className="text-lg font-semibold text-zinc-100">Task Templates</h1>
          {!loading && <span className="text-xs text-zinc-500">({visible.length})</span>}
        </div>

        <div className="flex items-center gap-2">
          {savedMsg && (
            <span role="status" className="flex items-center gap-1.5 text-xs text-emerald-400">
              <CheckCircle className="w-3.5 h-3.5" />
              {savedMsg}
            </span>
          )}
          <button
            type="button"
            onClick={() => void load()}
            disabled={loading}
            className="p-1.5 text-zinc-400 hover:text-zinc-200 transition-colors rounded-lg hover:bg-zinc-800"
            aria-label="Refresh task templates"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-3 mb-4">
        <input
          type="search"
          aria-label="Search task templates"
          placeholder="Search title or description"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className={`${selectClass} w-64`}
        />
        <select
          aria-label="Filter by category"
          value={filterCategory}
          onChange={(e) =>
            setFilterCategory(e.target.value === '' ? '' : (Number(e.target.value) as TaskCategory))
          }
          className={selectClass}
        >
          <option value="">All categories</option>
          {CATEGORY_OPTIONS.map((c) => (
            <option key={c} value={c}>{TASK_CATEGORY_LABELS[c]}</option>
          ))}
        </select>
        <select
          aria-label="Filter by active state"
          value={filterActive}
          onChange={(e) => setFilterActive(e.target.value as '' | 'true')}
          className={selectClass}
        >
          <option value="">Active & inactive</option>
          <option value="true">Active only</option>
        </select>
      </div>

      {error && (
        <div role="alert" className="flex items-center gap-3 p-3 mb-4 bg-red-500/10 border border-red-500/20 rounded-lg">
          <AlertCircle className="w-4 h-4 text-red-400 flex-shrink-0" />
          <p className="text-sm text-red-400 flex-1">{error}</p>
          <button type="button" onClick={() => setError(null)} className="text-zinc-500 hover:text-zinc-300" aria-label="Dismiss error">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {loading && (
        <div role="status" aria-label="Loading task templates" className="flex items-center justify-center h-32">
          <RefreshCw className="w-5 h-5 text-zinc-500 animate-spin" />
        </div>
      )}

      {!loading && !error && visible.length === 0 && (
        <div className="text-center py-10 border border-zinc-800 rounded-lg">
          <ListChecks className="w-8 h-8 text-zinc-700 mx-auto mb-2" />
          <p className="text-sm text-zinc-500">No task templates match the current filters.</p>
        </div>
      )}

      {!loading && visible.length > 0 && (
        <ul aria-label="Task templates" className="space-y-2">
          {visible.map((t) => (
            <li key={t.id}>
              {editTarget?.id === t.id ? (
                <TemplateEditForm
                  template={t}
                  onSave={handleSave}
                  onCancel={() => setEditTarget(null)}
                  saving={saving}
                />
              ) : (
                <div
                  className={`flex items-start gap-4 p-4 rounded-lg border ${
                    t.isActive ? 'border-zinc-700 bg-zinc-800/30' : 'border-zinc-800 bg-zinc-900/50 opacity-60'
                  }`}
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-medium text-zinc-200">{t.title}</span>
                      <span className="px-1.5 py-0.5 text-xs rounded border text-violet-400 bg-violet-500/10 border-violet-500/20">
                        {categoryLabel(t.category)}
                      </span>
                      <span className="px-1.5 py-0.5 text-xs rounded border border-zinc-700 text-zinc-400">
                        Version {t.version}
                      </span>
                      {!t.isActive && (
                        <span className="px-1.5 py-0.5 text-xs rounded border border-zinc-800 text-zinc-600">
                          Inactive
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-zinc-500 mt-1 truncate">{t.description}</p>
                    <p className="text-xs text-zinc-600 mt-1">
                      {t.estimatedHours} h · priority {t.priority}
                    </p>
                  </div>

                  <div className="flex items-center gap-1 flex-shrink-0">
                    <button
                      type="button"
                      onClick={() => setEditTarget(t)}
                      className="p-2 text-zinc-400 hover:text-zinc-200 transition-colors rounded-lg hover:bg-zinc-700"
                      aria-label={`Edit ${t.title}`}
                    >
                      <Edit2 className="w-4 h-4" />
                    </button>
                    <button
                      type="button"
                      onClick={() => void handleToggle(t)}
                      disabled={togglingId === t.id}
                      className="p-2 text-zinc-400 hover:text-zinc-200 transition-colors rounded-lg hover:bg-zinc-700 disabled:opacity-50"
                      aria-label={`${t.isActive ? 'Deactivate' : 'Activate'} ${t.title}`}
                    >
                      {togglingId === t.id ? (
                        <RefreshCw className="w-4 h-4 animate-spin" />
                      ) : t.isActive ? (
                        <PowerOff className="w-4 h-4" />
                      ) : (
                        <Power className="w-4 h-4" />
                      )}
                    </button>
                  </div>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </main>
  );
}

export default TaskTemplatesPage;
